import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, map, switchMap, of } from 'rxjs';
import { CrmInjectionToken, PAGE_ROUTES } from '@galaxy/crm/static';
import { FeatureFlagsApiService } from '@vendasta/partner';

@Injectable()
export class CustomObjectsGuard {
  private readonly config = inject(CrmInjectionToken);
  private readonly featureFlagService = inject(FeatureFlagsApiService);
  private readonly router = inject(Router);

  canActivate(): Observable<boolean | UrlTree> {
    const featureFlag = this.config.customObjectFeatureFlag ?? '';

    // If feature flag is defined and not empty, check it; otherwise skip feature flag check
    const hasFeatureFlag = featureFlag?.trim().length > 0;
    const featureFlagCheck$ = hasFeatureFlag
      ? combineLatest([this.config.namespace$, this.config.parentNamespace$]).pipe(
          switchMap(([namespace, parentNamespace]) => {
            return this.featureFlagService.batchGetStatus({
              featureIds: [featureFlag!],
              partnerId: (parentNamespace as string) || (namespace as string),
            });
          }),
          map((response) => response?.hasAccess?.[0] || false),
        )
      : of(true); // Pass feature flag check if not defined

    const isBusinessCenterClient = this.config.appID === 'business-center-client';

    // Check both feature flag (if defined) and custom objects access (includes SKU check)
    if (isBusinessCenterClient && this.config.showCustomObjects$) {
      return combineLatest([featureFlagCheck$, this.config.showCustomObjects$]).pipe(
        map(([hasFeatureFlag, hasCustomObjectsEnabled]) => {
          if (!hasFeatureFlag || !hasCustomObjectsEnabled) {
            return this.router.createUrlTree([PAGE_ROUTES.PERMISSION_REQUIRED]);
          }
          return true;
        }),
      );
    }

    // Fallback to feature flag only if custom objects check is not available
    return featureFlagCheck$.pipe(
      map((hasAccess) => {
        if (!hasAccess) {
          return this.router.createUrlTree([PAGE_ROUTES.PERMISSION_REQUIRED]);
        }
        return true;
      }),
    );
  }
}
